/**
 * Custom <Trans> wrapper with fallback logic.
 * 1. Tries to translate the given key in the current language.
 * 2. If missing, falls back to the English translation file.
 * 3. If still missing, uses a custom provided fallback string.
 * Includes a helper to safely access nested keys in the English translations.
 */
import { Trans, useTranslation } from 'react-i18next';
import type { ComponentProps } from 'react';
import { findTranslationKeyAsGivenJSONTranslationFallback } from '../../../shared/utils/helpers';

type TransWithFallbackProps = ComponentProps<typeof Trans> & {
  customFallbackTranslation?: string;
};

const TransWithFallback = ({ i18nKey, customFallbackTranslation = '', ...rest }: TransWithFallbackProps) => {
  const { i18n } = useTranslation();
  const keyExists = i18n.exists(i18nKey as string, { fallbackLng: [] });

  if (keyExists) {
    return <Trans i18nKey={i18nKey} {...rest} />;
  }

  const englishText = findTranslationKeyAsGivenJSONTranslationFallback(i18nKey as string);
  const fallback = englishText || customFallbackTranslation || '';

  return <Trans defaults={fallback} {...rest} />;
};

export default TransWithFallback;
